<template>
  <div v-if="showBanner" class="banner" :class="classes">
    <component
      :is="countdownLink ? 'a' : 'div'"
      v-show="showCountdownNow"
      :href="countdownLink"
      class="banner__countdown-wrapper"
    >
      <span v-if="countdownLabel" class="banner__label">
        {{ countdownLabel }}
      </span>
      <Countdown
        class="banner__countdown"
        :end="countdownEnd"
        :time-zone="countdownTimeZone"
        unit-labels
        hour-label="hrs"
      />
      <span v-if="countdownSuffix" class="banner__suffix">
        {{ countdownSuffix }}
      </span>
    </component>
    <span
      v-show="!showCountdownNow"
      class="banner__message"
      :class="{ 'banner__message--marquee': isMarqueeMessage }"
    >
      <template v-if="isMarqueeMessage">
        <span class="banner__marquee-track">
          <a
            v-if="displayMessage?.link"
            :href="displayMessage.link"
            class="banner__marquee-item banner__link"
          >
            {{ displayMessage.text }}
          </a>
          <span v-else class="banner__marquee-item">
            {{ displayMessage.text }}
          </span>
          <span class="banner__marquee-item" aria-hidden="true">
            {{ displayMessage.text }}
          </span>
        </span>
      </template>
      <template v-else>
        <a
          v-if="displayMessage?.link"
          :href="displayMessage.link"
          class="banner__link"
        >
          {{ displayMessage.text }}
        </a>
        <template v-else-if="displayMessage">{{
          displayMessage.text
        }}</template>
      </template>
    </span>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { bannerContent } from "@/config";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import Countdown from "@/components/landing/Countdown.vue";
export default {
  name: "Banner",
  components: {
    Countdown,
  },
  data() {
    return {
      currentSlotIndex: 0,
      rotationTimerId: null,
      now: Date.now(),
      countdownTimerId: null,
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    configForCurrency() {
      return bannerContent[this.currencyCode] || {};
    },
    color() {
      if (this.isCountdownActive && this.countdownConfig?.color) {
        return this.countdownConfig.color;
      }
      if (this.isLoggedIn && this.configForCurrency?.signedInColor) {
        return this.configForCurrency.signedInColor;
      }
      return this.configForCurrency?.color;
    },
    // The countdown takes the first slot, then each message in config order
    rotationSlots() {
      const slots = this.isCountdownActive ? [{ type: "countdown" }] : [];

      this.messages.forEach((message) => {
        const normalizedMessage = this.normalizeMessage(message);
        if (normalizedMessage) {
          slots.push({ type: "message", message: normalizedMessage });
        }
      });

      return slots;
    },
    currentSlot() {
      if (!this.rotationSlots.length) return null;
      return this.rotationSlots[
        this.currentSlotIndex % this.rotationSlots.length
      ];
    },
    rotateEnabled() {
      return !!this.configForCurrency?.rotate && this.rotationSlots.length > 1;
    },
    countdownConfig() {
      return this.configForCurrency?.countdown || null;
    },
    isCountdownActive() {
      if (!this.countdownConfig || !this.countdownConfig.end) return false;
      if (this.hasCountdownPassed) return false;

      const { bannerCountdownEnabled } = getFeatureToggles();

      if (typeof bannerCountdownEnabled === "boolean") {
        return bannerCountdownEnabled;
      }

      if (typeof this.countdownConfig.enabled === "boolean") {
        return this.countdownConfig.enabled;
      }

      return false;
    },
    messages() {
      const audienceMessages = this.isLoggedIn
        ? this.configForCurrency?.signedInMessages
        : this.configForCurrency?.signedOutMessages;

      let resolvedMessages = [];

      if (Array.isArray(audienceMessages) && audienceMessages.length) {
        resolvedMessages = audienceMessages;
      } else {
        const audienceMessage = this.isLoggedIn
          ? this.configForCurrency?.signedInMessage
          : this.configForCurrency?.signedOutMessage;

        if (audienceMessage) {
          resolvedMessages = [audienceMessage];
        } else if (
          Array.isArray(this.configForCurrency?.messages) &&
          this.configForCurrency.messages.length
        ) {
          resolvedMessages = this.configForCurrency.messages;
        } else if (this.configForCurrency?.message) {
          resolvedMessages = [this.configForCurrency.message];
        }
      }

      if (this.isLoggedIn && this.isLevel4Member) {
        return resolvedMessages.filter(
          (message) => !(message && message.doublePointsEvent)
        );
      }

      return resolvedMessages;
    },
    isLevel4Member() {
      const tags = this.customer?.tags || [];
      if (tags.includes("tier: Level 4")) {
        return true;
      }

      const loyaltyTierValue = this.customer?.loyaltyTier?.value;
      if (
        typeof loyaltyTierValue === "string" &&
        /level\s*4/i.test(loyaltyTierValue)
      ) {
        return true;
      }

      const loyaltyTierNumber =
        this.customer?.loyalty?.loyalty_tier_membership?.loyalty_tier?.number;
      return loyaltyTierNumber === 4;
    },
    displayMessage() {
      if (this.currentSlot?.type !== "message") return null;
      return this.currentSlot.message;
    },
    isMarqueeMessage() {
      return !!this.displayMessage?.marqueeOnMobile && !this.showCountdownNow;
    },
    countdownEnd() {
      return this.countdownConfig?.end || null;
    },
    hasCountdownPassed() {
      if (!this.countdownEnd) return false;
      const end = new Date(this.countdownEnd).getTime();
      if (Number.isNaN(end)) return false;
      return this.now >= end;
    },
    countdownLabel() {
      return this.countdownConfig?.label || "SALE STARTS";
    },
    countdownSuffix() {
      const { suffix } = this.countdownConfig || {};
      return suffix === undefined || suffix === null ? "Days" : suffix;
    },
    countdownTimeZone() {
      return this.countdownConfig?.timeZone || "Europe/London";
    },
    countdownLink() {
      return this.countdownConfig?.link || null;
    },
    showCountdownNow() {
      return this.isCountdownActive && this.currentSlot?.type === "countdown";
    },
    classes() {
      return {
        "banner--white": this.color === "white",
        "banner--black": this.color === "black",
        "banner--red": this.color === "red",
        "banner--marquee": this.isMarqueeMessage,
      };
    },
    showBanner() {
      const { bannerEnabled = true } = getFeatureToggles();
      const hasMessages = this.messages.length > 0;
      return bannerEnabled && (hasMessages || this.isCountdownActive);
    },
  },
  watch: {
    currencyCode: {
      handler() {
        this.resetRotation();
        this.startCountdownWatcher();
      },
    },
    isLoggedIn: {
      handler() {
        this.resetRotation();
      },
    },
    isLevel4Member: {
      handler() {
        this.resetRotation();
      },
    },
  },
  mounted() {
    this.startRotationIfNeeded();
    this.startCountdownWatcher();
  },
  beforeUnmount() {
    this.clearRotation();
    this.clearCountdownWatcher();
  },
  methods: {
    normalizeMessage(message) {
      if (!message) return null;
      if (typeof message === "string") {
        return { text: message, link: null, marqueeOnMobile: false };
      }
      if (typeof message === "object" && message.text) {
        return {
          text: message.text,
          link: message.link || null,
          marqueeOnMobile: !!message.marqueeOnMobile,
        };
      }
      return null;
    },
    startRotationIfNeeded() {
      this.clearRotation();
      this.currentSlotIndex = 0;

      if (!this.rotateEnabled) return;

      const intervalMs =
        Number(this.configForCurrency?.rotationIntervalMs) || 4000;

      this.rotationTimerId = setInterval(() => {
        this.currentSlotIndex =
          (this.currentSlotIndex + 1) % this.rotationSlots.length;
      }, intervalMs);
    },
    clearRotation() {
      if (this.rotationTimerId) {
        clearInterval(this.rotationTimerId);
        this.rotationTimerId = null;
      }
    },
    resetRotation() {
      this.startRotationIfNeeded();
    },
    startCountdownWatcher() {
      this.clearCountdownWatcher();
      if (!this.countdownEnd || this.hasCountdownPassed) return;
      this.countdownTimerId = setInterval(() => {
        this.now = Date.now();
        if (this.hasCountdownPassed) {
          this.clearCountdownWatcher();
        }
      }, 1000);
    },
    clearCountdownWatcher() {
      if (this.countdownTimerId) {
        clearInterval(this.countdownTimerId);
        this.countdownTimerId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.banner {
  align-items: center;
  display: flex;
  min-height: 34px;
  height: auto;
  @include utility-body(15px);
  justify-content: center;
  letter-spacing: 0.65px;
  text-decoration: none;
  padding: 6px 12px;
  text-align: center;

  &__label,
  &__suffix {
    @include utility-body(15px);
    margin: 0 4px;
    letter-spacing: 0.65px;
    line-height: 1.3;
    text-transform: none;
    white-space: nowrap;
  }

  &__suffix {
    margin-left: 2px;
  }

  &__countdown {
    margin: 0 0 0 4px;
    :deep(.countdown) {
      .unit,
      .divider {
        flex: 0 0 auto;
      }

      .unit {
        margin: 0 2px;
      }

      .divider {
        margin: 0 2px;
      }

      .value,
      .divider,
      .unit-label {
        @include utility-body(15px);
        letter-spacing: 0.65px;
        line-height: 1.3;
      }
    }
  }

  &__countdown-wrapper {
    display: flex;
    align-items: center;
    color: inherit;
    text-decoration: none;

    &:hover {
      opacity: 0.85;
    }
  }

  &--white {
    background-color: $color-white;
    color: $color-black;
  }

  &--black {
    background-color: $color-black;
    color: $color-white;
  }

  &--red {
    background-color: #d0021b;
    color: $color-white;
    text-align: center;
  }

  &__message {
    @include utility-body(15px);
    letter-spacing: 0.65px;
    white-space: normal;
    text-wrap: pretty;
    line-height: 1.3;
  }

  &__marquee-track {
    display: contents;
  }

  &__marquee-item {
    white-space: nowrap;

    &[aria-hidden="true"] {
      display: none;
    }
  }

  &__link {
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    font-style: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      opacity: 0.85;
    }
  }

  &--marquee {
    @include respond-below(md) {
      height: 34px;
      min-height: 34px;
      padding-left: 0;
      padding-right: 0;
      overflow: hidden;

      .banner__message--marquee {
        display: block;
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-align: left;
        text-wrap: nowrap;
        line-height: 34px;
      }

      .banner__marquee-track {
        display: inline-flex;
        gap: 48px;
        width: max-content;
        animation: banner-marquee 22s linear infinite;
      }

      .banner__marquee-item {
        flex-shrink: 0;

        &[aria-hidden="true"] {
          display: inline;
        }
      }
    }
  }

  @include respond-above(md) {
    min-height: 36px;
    height: 36px;
    padding: 0 5px;

    &__message {
      white-space: nowrap;
      line-height: inherit;
    }
  }
}

@keyframes banner-marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-50% - 24px));
  }
}
</style>
